import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Chip, Divider, IconButton, Stack } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import {
  FLOW_REST_METHOD_AND_ENDPOINT_HELPER_TEXT,
  type FlowRestBlock,
  FlowRestRequestBodyFormat,
  FlowRestResponseBodyFormat,
} from 'flow-schema';
import { startCase } from 'lodash';
import { Link as ReactRouterLink } from 'react-router-dom';

import { api, getQueryProps, isDefined } from 'common';

import { useFlowDefinitionContext } from '../../../../../context';
import {
  BlockAutocomplete,
  BlockFreeSoloMultiAutocomplete,
} from '../../../ConfigFields/Autocomplete';
import {
  BlockValueTemplateField,
  BlockValueTemplateFieldArea,
} from '../../../ConfigFields/CodeMirror/FlowTemplate';
import { BlockKeyValuesField } from '../../../ConfigFields/KeyValues/KeyValuesField';
import { BlockRadioGroupField } from '../../../ConfigFields/RadioGroupField';
import { BlockSelectField } from '../../../ConfigFields/SelectField';
import { useSelectedNodeField } from '../../../ConfigFields/useSelectedNodeField';

type RestBlockConfig = FlowRestBlock['config'];

const { extractDomain } = api.FlowDefinition;
const RequestBodyFormat = FlowRestRequestBodyFormat;

interface SharedRestBlockFormFieldsProps {
  fieldNamePrefix?: string;
}

const bodyFormatOptions = [
  { label: 'None', value: RequestBodyFormat.None },
  { label: 'JSON', value: RequestBodyFormat.RawJson },
  { label: 'Form URL Encoded', value: RequestBodyFormat.FormUrlEncoded },
];

export function SharedRestBlockFormFields(props: SharedRestBlockFormFieldsProps) {
  const { fieldNamePrefix = 'config' } = props;
  const {
    flowDefinition: { organizationId },
  } = useFlowDefinitionContext();
  const { value: methodValue } = useSelectedNodeField<RestBlockConfig['method']>(
    `${fieldNamePrefix}.method`
  );
  const { value: endpointValue } = useSelectedNodeField<RestBlockConfig['endpoint']>(
    `${fieldNamePrefix}.endpoint`
  );
  const { value: selectedOptionsValue } = useSelectedNodeField<RestBlockConfig['secureHeaders']>(
    `${fieldNamePrefix}.secureHeaders`
  );
  const { value: requestBodyFormatValue } = useSelectedNodeField<RestBlockConfig['bodyFormat']>(
    `${fieldNamePrefix}.bodyFormat`
  );

  const currentDomain = extractDomain(endpointValue);

  const { data: domainOptions = [] } = useQuery({
    ...getQueryProps(api.client.SecureHeaders, 'findAll', {
      organizationId,
      domain: currentDomain,
    }),
    enabled: currentDomain.length >= 4,
  });

  const { data: selectedOptions = [] } = useQuery({
    ...getQueryProps(api.client.SecureHeaders, 'findAll', {
      organizationId,
      ids: selectedOptionsValue?.map((selectedOption) => selectedOption.id),
    }),
    enabled: (selectedOptionsValue?.length ?? 0) > 0,
  });

  // Combine selected and domain options. When loading from an existing definition,
  // the form value may only contain partial secureHeader objects (just id), so we
  // need to resolve them against fetched data in renderValue
  const combinedOptions = [...selectedOptions, ...domainOptions];

  const doesMethodSupportBody =
    isDefined(methodValue) && ['POST', 'PUT', 'PATCH'].includes(methodValue);

  return (
    <>
      <Stack gap={2}>
        <Stack direction="row" width="100%" spacing={1}>
          <BlockSelectField
            fieldName={`${fieldNamePrefix}.method`}
            label="Method"
            options={['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].map((method) => ({
              label: method,
              value: method,
            }))}
            required
          />

          <BlockValueTemplateField
            fieldName={`${fieldNamePrefix}.endpoint`}
            label="Endpoint"
            required
            helperText={FLOW_REST_METHOD_AND_ENDPOINT_HELPER_TEXT}
            isMultiline
          />
        </Stack>
        <Stack direction="row" width="100%" alignItems="flex-end">
          <Stack flexGrow={1}>
            <BlockAutocomplete
              fieldName={`${fieldNamePrefix}.secureHeaders`}
              label="Secure Headers"
              options={combinedOptions}
              getOptionKey={(option) => option.id}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              filterSelectedOptions
              renderValue={(value, getItemProps) =>
                value.map((option, index) => {
                  const resolvedOption =
                    combinedOptions.find((opt) => opt.id === option.id) ?? option;
                  return (
                    <Chip
                      {...getItemProps({ index })}
                      key={option.id}
                      label={resolvedOption.name ?? option.id}
                      color={resolvedOption.domain !== currentDomain ? 'error' : 'default'}
                      variant="outlined"
                    />
                  );
                })
              }
              freeSolo={false}
              multiple
              required
            />
          </Stack>
          <IconButton
            component={ReactRouterLink}
            to="/agents/secureHeaders"
            target="_blank"
            rel="noopener noreferrer"
          >
            <OpenInNewIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Stack>
      <BlockSelectField
        fieldName={`${fieldNamePrefix}.responseBodyFormat`}
        label="Response Body Format"
        options={Object.values(FlowRestResponseBodyFormat).map((format) => ({
          label: startCase(format),
          value: format,
        }))}
        defaultValue={FlowRestResponseBodyFormat.AutoDetect}
      />
      <Divider />
      <Stack gap={2}>
        <BlockValueTemplateField
          fieldName={`${fieldNamePrefix}.params`}
          label="Params"
          helperText="Enter API Request Params."
        />
        <Divider />
        <BlockKeyValuesField
          fieldName={`${fieldNamePrefix}.headers`}
          addRowHelperText="header"
          label="Headers"
          helperText="Enter API Request Headers."
        />
        <BlockFreeSoloMultiAutocomplete
          fieldName={`${fieldNamePrefix}.selectedResponseHeaders`}
          label="Response Headers"
          helperText="Enter response headers to be returned."
        />
        {doesMethodSupportBody && (
          <>
            <Divider />
            <BlockRadioGroupField
              options={bodyFormatOptions}
              label="Request Body"
              fieldName={`${fieldNamePrefix}.bodyFormat`}
              defaultValue={RequestBodyFormat.RawJson}
              row
            />
            {requestBodyFormatValue !== RequestBodyFormat.None && (
              <BlockValueTemplateFieldArea
                fieldName={`${fieldNamePrefix}.body`}
                label="Body"
                helperText="Enter API Request Body."
              />
            )}
          </>
        )}
      </Stack>
    </>
  );
}
